import React, { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import FormSection from '../components/FormSection'
import {
  createBidPackage,
  fetchPackageReview,
  fetchScopeFirstReview,
  updateBidPackage
} from '../services/procurementBidsClient'
import { prepareVendorPortalUpload } from '../services/procurementPortalClient'
import { listAllUnits } from '../services/unitsClient'
import { searchVendors } from '../services/vendorsClient'
import {
  buildPlanBasedSbfFilename,
  buildPlanBasedSbfWorkbook,
  buildPlanBasedSbfWorkbookFileData,
  buildVendorDraftEmail,
  buildVendorPortalUrl,
  buildIssuedRfpPackageWritePayload,
  PROCUREMENT_DELIVERY_QUARTER_OPTIONS,
  buildProjectOptions,
  buildRfpDraftModel,
  buildScopeTradeLabelContract,
  buildScopeTradeLabelOptions,
  buildVendorSearchTermsForScope,
  buildVendorOption,
  PROCUREMENT_SEND_OUTPUT_CLASSIFICATION,
  PROCUREMENT_SBF_TEMPLATE_FAMILY
} from '../utils/procurementRfp'
import {
  filterScopePickerOptions,
  formatScopePickerOptionLabel
} from '../utils/procurementScopePicker'

let procurementLogoBytesPromise = null

function formatProjectOptionLabel(project) {
  if (!project) {
    return '--'
  }

  return [
    project.project_id,
    `${project.unit_count} units`,
    `${project.building_count} buildings`,
    `${project.plan_count} plans`,
    project.project_address || null
  ].filter(Boolean).join(' | ')
}

function parseListText(value) {
  return String(value || '')
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function createObjectDownload(fileName, content, mimeType) {
  const blob = new Blob([content], { type: mimeType })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

async function loadProcurementLogoBytes() {
  if (!procurementLogoBytesPromise) {
    procurementLogoBytesPromise = fetch('/assets/hbfa-logo.png')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load the HBFA logo asset')
        }

        return response.arrayBuffer()
      })
      .then((buffer) => new Uint8Array(buffer))
  }

  return procurementLogoBytesPromise
}

function openDraftWindow(draftUrl) {
  const draftWindow = window.open(draftUrl, '_blank', 'noopener,noreferrer')
  if (!draftWindow) {
    window.location.assign(draftUrl)
  }
}

function isMissingPackageReviewError(error) {
  const message = String(error?.message || '').trim().toLowerCase()
  return message === 'bid package not found'
}

function resolveVendorPortalBaseUrl() {
  const configuredBaseUrl = String(import.meta.env.VITE_VENDOR_PORTAL_URL || '').trim()
  if (configuredBaseUrl) {
    return configuredBaseUrl
  }

  return new URL('/vendor-portal.html', window.location.origin).toString()
}

function createInitialFormState() {
  return {
    project_id: '',
    scope_id: '',
    selected_trade_label: '',
    due_at: '',
    selected_vendor_ids: [],
    building_exclusions: [],
    unit_exclusions: [],
    project_name_override: '',
    project_address_override: '',
    scope_summary: '',
    scope_detail: '',
    home_type_label: '',
    delivery_start_quarter: '',
    delivery_start_year: '',
    delivery_summary_override: '',
    box_links_text: '',
    plan_links_text: '',
    rfp_notes: '',
    vendor_filter: ''
  }
}

const ProcurementRfpDesigner = ({ onNavigate }) => {
  const { user } = useAuth()
  const [allUnits, setAllUnits] = useState([])
  const [canonicalScopeOptions, setCanonicalScopeOptions] = useState([])
  const [vendors, setVendors] = useState([])
  const [form, setForm] = useState(createInitialFormState)
  const [scopeSearch, setScopeSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [vendorLoading, setVendorLoading] = useState(false)
  const [vendorError, setVendorError] = useState('')
  const [packageReviewLoading, setPackageReviewLoading] = useState(false)
  const [packageReviewError, setPackageReviewError] = useState('')
  const [packageRecord, setPackageRecord] = useState(null)
  const [packageIssueLoading, setPackageIssueLoading] = useState(false)
  const [packageIssueError, setPackageIssueError] = useState('')
  const [packageIssueMessage, setPackageIssueMessage] = useState('')
  const [sendingVendorId, setSendingVendorId] = useState('')
  const [preparedPortalLinks, setPreparedPortalLinks] = useState({})

  useEffect(() => {
    let cancelled = false

    async function loadInitialData() {
      setLoading(true)
      setError('')

      try {
        const [unitsResponse, scopeResponse] = await Promise.all([
          listAllUnits(),
          fetchScopeFirstReview()
        ])

        if (cancelled) {
          return
        }

        const unitItems = Array.isArray(unitsResponse?.items) ? unitsResponse.items : []
        const scopeItems = Array.isArray(scopeResponse?.canonical_scope_options)
          ? scopeResponse.canonical_scope_options
          : []

        setAllUnits(unitItems)
        setCanonicalScopeOptions(scopeItems)

        const projects = buildProjectOptions(unitItems)
        setForm((current) => ({
          ...current,
          project_id: current.project_id || projects[0]?.project_id || ''
        }))
      } catch (loadError) {
        console.error('Procurement RFP designer load failed:', loadError)
        if (!cancelled) {
          setError(loadError.message || 'Failed to load procurement RFP setup data')
          setAllUnits([])
          setCanonicalScopeOptions([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadInitialData()
    return () => {
      cancelled = true
    }
  }, [])

  const projectOptions = buildProjectOptions(allUnits)
  const filteredScopeOptions = filterScopePickerOptions(canonicalScopeOptions, scopeSearch)
  const selectedProject = projectOptions.find((project) => project.project_id === form.project_id) || null
  const selectedScope = canonicalScopeOptions.find((scope) => scope.scope_id === form.scope_id) || null
  const tradeLabelContract = buildScopeTradeLabelContract(selectedScope, form.selected_trade_label)
  const tradeLabelOptions = tradeLabelContract.trade_label_options

  useEffect(() => {
    const scopeTradeLabelOptions = buildScopeTradeLabelOptions(selectedScope)

    if (!selectedScope) {
      if (form.selected_trade_label || vendors.length) {
        setForm((current) => ({
          ...current,
          selected_trade_label: '',
          selected_vendor_ids: []
        }))
        setVendors([])
      }
      return
    }

    const nextTradeLabel = scopeTradeLabelOptions.includes(form.selected_trade_label)
      ? form.selected_trade_label
      : (scopeTradeLabelOptions[0] || '')

    if (nextTradeLabel !== form.selected_trade_label) {
      setForm((current) => ({
        ...current,
        selected_trade_label: nextTradeLabel,
        selected_vendor_ids: []
      }))
    }
  }, [form.selected_trade_label, selectedScope, vendors.length])

  useEffect(() => {
    let cancelled = false
    const searchTerms = buildVendorSearchTermsForScope(selectedScope, form.selected_trade_label)

    async function loadVendors() {
      if (!searchTerms.length) {
        setVendors([])
        setVendorError('')
        return
      }

      setVendorLoading(true)
      setVendorError('')

      try {
        const searchResults = await Promise.all(
          searchTerms.map((term) => searchVendors({ type: 'scope', term }))
        )
        if (cancelled) {
          return
        }

        const vendorItems = Array.from(new Map(
          searchResults
            .flatMap((result) => result?.vendors || [])
            .map((vendor) => {
              const vendorOption = buildVendorOption(vendor)
              return [vendorOption.vendor_id, vendorOption]
            })
        ).values())
          .filter((vendor) => vendor.status.toLowerCase() !== 'inactive')

        setVendors(vendorItems)
        setForm((current) => ({
          ...current,
          selected_vendor_ids: current.selected_vendor_ids.filter((vendorId) => (
            vendorItems.some((vendor) => vendor.vendor_id === vendorId)
          ))
        }))
      } catch (loadError) {
        console.error('Procurement RFP vendor load failed:', loadError)
        if (!cancelled) {
          setVendorError(loadError.message || 'Failed to load matching vendors')
          setVendors([])
          setForm((current) => ({
            ...current,
            selected_vendor_ids: []
          }))
        }
      } finally {
        if (!cancelled) {
          setVendorLoading(false)
        }
      }
    }

    loadVendors()
    return () => {
      cancelled = true
    }
  }, [form.scope_id, form.selected_trade_label])

  const visibleVendors = vendors.filter((vendor) => {
    const filter = String(form.vendor_filter || '').trim().toLowerCase()
    if (!filter) {
      return true
    }

    return [vendor.company_name, vendor.trade, vendor.scope_ids.join(' '), vendor.scopes.join(' ')]
      .join(' ')
      .toLowerCase()
      .includes(filter)
  })

  const rfpModel = buildRfpDraftModel({
    allUnits,
    canonicalScopeOptions,
    vendors,
    projectId: form.project_id,
    scopeId: form.scope_id,
    selectedTradeLabel: form.selected_trade_label,
    selectedVendorIds: form.selected_vendor_ids,
    dueAt: form.due_at,
    buildingExclusions: form.building_exclusions,
    unitExclusions: form.unit_exclusions,
    scopeSummary: form.scope_summary,
    scopeDetail: form.scope_detail,
    homeTypeLabel: form.home_type_label,
    deliveryStartQuarter: form.delivery_start_quarter,
    deliveryStartYear: form.delivery_start_year,
    deliverySummaryOverride: form.delivery_summary_override,
    boxLinks: parseListText(form.box_links_text),
    planLinks: parseListText(form.plan_links_text),
    rfpNotes: form.rfp_notes,
    projectNameOverride: form.project_name_override,
    projectAddressOverride: form.project_address_override
  })

  const selectedVendorSet = new Set(form.selected_vendor_ids)
  const includedBuildingSet = new Set(rfpModel.project?.included_buildings || [])

  useEffect(() => {
    let cancelled = false
    const packageId = rfpModel.package_draft?.package_id
    const hasPackageContext = Boolean(
      rfpModel.project?.project_id
      && rfpModel.scope?.scope_id
      && rfpModel.package_context?.due_at
    )

    setPackageIssueError('')
    setPackageIssueMessage('')

    if (!hasPackageContext || !packageId) {
      setPackageRecord(null)
      setPackageReviewError('')
      setPackageReviewLoading(false)
      return () => {
        cancelled = true
      }
    }

    async function loadPackageRecord() {
      setPackageReviewLoading(true)
      setPackageReviewError('')

      try {
        const packageReview = await fetchPackageReview(packageId)
        if (cancelled) {
          return
        }

        const selectedPackage = packageReview?.selected_package?.package_id === packageId
          ? packageReview.selected_package
          : null
        setPackageRecord(selectedPackage)
      } catch (loadError) {
        console.error('Procurement RFP package review load failed:', loadError)
        if (!cancelled) {
          if (isMissingPackageReviewError(loadError)) {
            setPackageRecord(null)
            setPackageReviewError('')
          } else {
            setPackageRecord(null)
            setPackageReviewError(loadError.message || 'Failed to load package issuance state')
          }
        }
      } finally {
        if (!cancelled) {
          setPackageReviewLoading(false)
        }
      }
    }

    loadPackageRecord()
    return () => {
      cancelled = true
    }
  }, [
    rfpModel.package_context?.due_at,
    rfpModel.package_draft?.package_id,
    rfpModel.project?.project_id,
    rfpModel.scope?.scope_id
  ])

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value
    }))
  }

  function toggleSelectedValue(field, value) {
    setForm((current) => {
      const existing = new Set(current[field])
      if (existing.has(value)) {
        existing.delete(value)
      } else {
        existing.add(value)
      }

      return {
        ...current,
        [field]: Array.from(existing).sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }))
      }
    })
  }

  function handleScopeChange(value) {
    setForm((current) => ({
      ...current,
      scope_id: value,
      selected_trade_label: '',
      selected_vendor_ids: []
    }))
  }

  function handleDownloadPackageContext() {
    const payload = {
      template_family: rfpModel.template_family,
      project: rfpModel.project,
      scope: rfpModel.scope,
      vendors: rfpModel.vendors,
      package_context: rfpModel.package_context,
      plan_rows: rfpModel.plan_rows,
      send_groups: rfpModel.send_groups
    }

    createObjectDownload(
      `${rfpModel.project?.project_id || 'project'}-${rfpModel.scope?.scope_id || 'scope'}-rfp-context.json`,
      `${JSON.stringify(payload, null, 2)}\n`,
      'application/json'
    )
  }

  async function prepareVendorPortalLink(sendGroup) {
    if (!sendGroup?.vendor_id) {
      return ''
    }

    if (preparedPortalLinks[sendGroup.vendor_id]) {
      return preparedPortalLinks[sendGroup.vendor_id]
    }

    if (!packageIsIssued || !packageRecord?.package_id) {
      return ''
    }

    const portalPrep = await prepareVendorPortalUpload({
      package_id: packageRecord.package_id,
      vendor_id: sendGroup.vendor_id,
      scope_ids: sendGroup.portal_preparation.scope_ids,
      template_family: rfpModel.template_family,
      portal_submission_id: sendGroup.portal_preparation.portal_submission_id,
      sbf_artifact_id: sendGroup.portal_preparation.sbf_artifact_id,
      completed_sbf: sendGroup.portal_preparation.completed_sbf,
      attachments: sendGroup.portal_preparation.attachments
    })
    const portalUrl = buildVendorPortalUrl({
      package_id: portalPrep.package_id,
      vendor_id: portalPrep.vendor_id,
      portal_submission_id: portalPrep.portal_submission_id,
      sbf_artifact_id: portalPrep.sbf_artifact_id
    }, {
      baseUrl: resolveVendorPortalBaseUrl()
    })

    setPreparedPortalLinks((current) => ({
      ...current,
      [sendGroup.vendor_id]: portalUrl
    }))

    return portalUrl
  }

  async function handleDownloadVendorWorkbook(vendor) {
    try {
      const artifactId = vendor?.portal_preparation?.sbf_artifact_id || undefined
      const portalUrl = await prepareVendorPortalLink(vendor)
      const workbook = buildPlanBasedSbfWorkbook(rfpModel, vendor, {
        artifactId,
        portalUrl
      })
      const fileName = buildPlanBasedSbfFilename({
        projectId: rfpModel.project?.project_id,
        scopeId: rfpModel.scope?.scope_id,
        vendorId: vendor.vendor_id
      })
      const workbookBytes = buildPlanBasedSbfWorkbookFileData(workbook, {
        logoPngBytes: await loadProcurementLogoBytes()
      })

      createObjectDownload(
        fileName,
        workbookBytes,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    } catch (downloadError) {
      console.error('Procurement workbook download failed:', downloadError)
      setError(downloadError.message || 'Failed to generate the procurement workbook')
    }
  }

  const packageIsIssued = String(packageRecord?.status || '').trim().toLowerCase() === 'issued'
    || Boolean(packageRecord?.issued_at)
  const packageIssueDisabled = packageIssueLoading
    || packageReviewLoading
    || packageIsIssued
    || !rfpModel.project?.project_id
    || !rfpModel.scope?.scope_id
    || !rfpModel.package_context?.due_at
    || !rfpModel.send_groups.length

  async function handleIssuePackage() {
    if (!rfpModel.project?.project_id || !rfpModel.scope?.scope_id) {
      setPackageIssueError('Select a project and scope before issuing a package')
      return
    }

    if (!rfpModel.package_context?.due_at) {
      setPackageIssueError('Set the bid due date before issuing a package')
      return
    }

    if (!rfpModel.send_groups.length) {
      setPackageIssueError('Select at least one vendor before issuing a package')
      return
    }

    setPackageIssueLoading(true)
    setPackageIssueError('')
    setPackageIssueMessage('')

    try {
      const packageId = rfpModel.package_draft.package_id
      let existingPackage = null

      try {
        const packageReview = await fetchPackageReview(packageId)
        existingPackage = packageReview?.selected_package?.package_id === packageId
          ? packageReview.selected_package
          : null
      } catch (loadError) {
        if (!isMissingPackageReviewError(loadError)) {
          throw loadError
        }
      }

      if (existingPackage?.edit_locked) {
        throw new Error('Package issuance is locked once bid responses exist for this package')
      }

      if (String(existingPackage?.status || '').trim().toLowerCase() === 'issued' || existingPackage?.issued_at) {
        setPackageRecord(existingPackage)
        setPackageIssueMessage(`Package ${packageId} is already issued.`)
        return
      }

      const payload = buildIssuedRfpPackageWritePayload(rfpModel.package_draft, {
        existingPackage,
        issuedAt: new Date().toISOString(),
        createdBy: user?.email || ''
      })

      if (existingPackage) {
        await updateBidPackage(packageId, payload)
      } else {
        await createBidPackage(payload)
      }

      const refreshedReview = await fetchPackageReview(packageId)
      const refreshedPackage = refreshedReview?.selected_package?.package_id === packageId
        ? refreshedReview.selected_package
        : payload

      setPackageRecord(refreshedPackage)
      setPackageIssueMessage(
        existingPackage
          ? `Issued package ${packageId}.`
          : `Created and issued package ${packageId}.`
      )
    } catch (issueError) {
      console.error('Procurement package issuance failed:', issueError)
      setPackageIssueError(issueError.message || 'Failed to issue the procurement package')
    } finally {
      setPackageIssueLoading(false)
    }
  }

  async function handleOpenVendorDraft(sendGroup) {
    if (!sendGroup?.resolved_emails?.length) {
      setError('Select a vendor with at least one resolved email before opening the draft')
      return
    }

    if (!packageIsIssued || !packageRecord?.package_id) {
      setError('Create and issue the package before Send prepares the vendor portal link')
      return
    }

    setSendingVendorId(sendGroup.vendor_id)
    setError('')

    try {
      const portalUrl = await prepareVendorPortalLink(sendGroup)
      const draftEmail = buildVendorDraftEmail(rfpModel, sendGroup, {
        portalUrl
      })

      if (!draftEmail?.outlook_compose_url) {
        throw new Error('Failed to build the Outlook draft URL for this vendor send group')
      }
      openDraftWindow(draftEmail.outlook_compose_url)
    } catch (draftError) {
      console.error('Procurement vendor draft prep failed:', draftError)
      setError(draftError.message || 'Failed to prepare the vendor portal draft')
    } finally {
      setSendingVendorId('')
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Procurement RFP Designer</h2>
        <p>
          First-slice internal RFP designer for `{PROCUREMENT_SBF_TEMPLATE_FAMILY}`. This page
          stays bounded to project selection, scope selection, vendor multi-select, building/unit
          exclusions, package-context capture, and per-vendor `.xlsx` SBF generation.
        </p>
      </div>

      <FormSection title="Actions">
        <div className="actions procurement-review-actions">
          <button type="button" className="btn" onClick={() => onNavigate?.('dashboard')}>
            Back to Dashboard
          </button>
          <button type="button" className="btn" onClick={() => onNavigate?.('procurement-review')}>
            Procurement Review
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={handleDownloadPackageContext}
            disabled={!rfpModel.project?.project_id || !rfpModel.scope?.scope_id}
          >
            Download Package Context JSON
          </button>
        </div>
      </FormSection>

      {loading ? (
        <FormSection title="Loading">
          <div className="procurement-review-note">Loading units, scopes, and the first-slice RFP designer context...</div>
        </FormSection>
      ) : null}

      {error ? (
        <FormSection title="Load Error">
          <div className="banner warn"><p>{error}</p></div>
        </FormSection>
      ) : null}

      {!loading && !error ? (
        <>
          <FormSection title="Project Setup">
            <div className="procurement-review-form">
              <label className="procurement-review-input-group">
                <span className="text-strong">Project</span>
                <select value={form.project_id} onChange={(event) => updateField('project_id', event.target.value)}>
                  <option value="">Select a project</option>
                  {projectOptions.map((project) => (
                    <option key={project.project_id} value={project.project_id}>
                      {formatProjectOptionLabel(project)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Project Name Override</span>
                <input
                  type="text"
                  value={form.project_name_override}
                  onChange={(event) => updateField('project_name_override', event.target.value)}
                  placeholder={selectedProject?.project_id || 'Defaults to project_id'}
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Project Address Override</span>
                <input
                  type="text"
                  value={form.project_address_override}
                  onChange={(event) => updateField('project_address_override', event.target.value)}
                  placeholder={selectedProject?.project_address || 'Hydrated if one unique address exists'}
                />
              </label>
            </div>

            {selectedProject ? (
              <div className="procurement-review-summary-grid">
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Units</span>
                  <span className="procurement-review-stat-value">{selectedProject.unit_count}</span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Buildings</span>
                  <span className="procurement-review-stat-value">{selectedProject.building_count}</span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Plans</span>
                  <span className="procurement-review-stat-value">{selectedProject.plan_count}</span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Hydrated Address</span>
                  <span className="procurement-review-stat-value procurement-review-stat-value--small">{selectedProject.project_address || '--'}</span>
                </div>
              </div>
            ) : null}
          </FormSection>

          <FormSection title="Scope And Package Context">
            <div className="procurement-review-form">
              <label className="procurement-review-input-group">
                <span className="text-strong">Scope / Trade Search</span>
                <input
                  type="text"
                  value={scopeSearch}
                  onChange={(event) => setScopeSearch(event.target.value)}
                  placeholder="Search by trade/common name, canonical scope, or id"
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Canonical Scope</span>
                <select value={form.scope_id} onChange={(event) => handleScopeChange(event.target.value)}>
                  <option value="">Select a scope</option>
                  {filteredScopeOptions.map((scope) => (
                    <option key={scope.scope_id} value={scope.scope_id}>
                      {formatScopePickerOptionLabel(scope)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Vendor-facing Trade Label</span>
                <select
                  value={form.selected_trade_label}
                  onChange={(event) => updateField('selected_trade_label', event.target.value)}
                  disabled={!selectedScope}
                >
                  <option value="">Select a trade label</option>
                  {tradeLabelOptions.map((tradeLabel) => (
                    <option key={tradeLabel} value={tradeLabel}>{tradeLabel}</option>
                  ))}
                </select>
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Bid Due Date</span>
                <input type="date" value={form.due_at} onChange={(event) => updateField('due_at', event.target.value)} />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Scope Summary</span>
                <textarea
                  rows="4"
                  value={form.scope_summary}
                  onChange={(event) => updateField('scope_summary', event.target.value)}
                  placeholder="Project-specific narrative that belongs in package-context JSON or S3-backed context."
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Scope Detail</span>
                <textarea
                  rows="5"
                  value={form.scope_detail}
                  onChange={(event) => updateField('scope_detail', event.target.value)}
                  placeholder="Optional longer detail for the vendor draft email body when scope_summary alone is too thin."
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Home Type Label</span>
                <input
                  type="text"
                  value={form.home_type_label}
                  onChange={(event) => updateField('home_type_label', event.target.value)}
                  placeholder="townhomes"
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Delivery Start Quarter</span>
                <select
                  value={form.delivery_start_quarter}
                  onChange={(event) => updateField('delivery_start_quarter', event.target.value)}
                >
                  <option value="">Select quarter</option>
                  {PROCUREMENT_DELIVERY_QUARTER_OPTIONS.map((quarter) => (
                    <option key={quarter} value={quarter}>{quarter}</option>
                  ))}
                </select>
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Delivery Start Year</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.delivery_start_year}
                  onChange={(event) => updateField('delivery_start_year', event.target.value)}
                  placeholder="2026"
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Delivery Summary Override</span>
                <input
                  type="text"
                  value={form.delivery_summary_override}
                  onChange={(event) => updateField('delivery_summary_override', event.target.value)}
                  placeholder="Q3 2026"
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Box Links</span>
                <textarea
                  rows="3"
                  value={form.box_links_text}
                  onChange={(event) => updateField('box_links_text', event.target.value)}
                  placeholder="One per line"
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">Plan Links</span>
                <textarea
                  rows="3"
                  value={form.plan_links_text}
                  onChange={(event) => updateField('plan_links_text', event.target.value)}
                  placeholder="One per line"
                />
              </label>

              <label className="procurement-review-input-group">
                <span className="text-strong">RFP Notes</span>
                <textarea
                  rows="3"
                  value={form.rfp_notes}
                  onChange={(event) => updateField('rfp_notes', event.target.value)}
                  placeholder="Internal instruction that should appear on the generated vendor-facing SBF."
                />
              </label>
            </div>

            {selectedScope ? (
              <div className="procurement-review-note">
                {formatScopePickerOptionLabel(selectedScope)}
                {selectedScope.trade_tags?.length ? ` | trade tags: ${selectedScope.trade_tags.join(', ')}` : ''}
                {tradeLabelContract.resolved_trade_label
                  ? ` | trade label contract: ${tradeLabelContract.resolved_trade_label} from ${tradeLabelContract.source_field}`
                  : ''}
              </div>
            ) : null}

            <div className="procurement-review-note">
              Scope search here is now driven by the canonical scope glossary, including
              `common_names[]`. Vendor-facing trade label selection still comes from `trade_tags[]`.
            </div>
          </FormSection>

          <FormSection title="Vendor Multi-select">
            <div className="procurement-review-form">
              <label className="procurement-review-input-group">
                <span className="text-strong">Filter Matching Vendors</span>
                <input
                  type="text"
                  value={form.vendor_filter}
                  onChange={(event) => updateField('vendor_filter', event.target.value)}
                  placeholder="Filter by company, trade, or scope"
                />
              </label>
            </div>

            <div className="procurement-review-note">
              Matching vendors are resolved from the existing vendor search API using the selected
              canonical scope plus the vendor-facing trade label derived from
              <code> trade_tags[] </code>. This slice does not assume a live
              <code> trade_tags_normalized </code> field. Send groups preserve one vendor row and
              all resolved contact emails by default.
            </div>

            {vendorLoading ? <div className="procurement-review-empty">Loading matching vendors...</div> : null}
            {vendorError ? <div className="banner warn"><p>{vendorError}</p></div> : null}

            {!vendorLoading && !vendorError ? (
              <div className="procurement-review-response-list">
                {visibleVendors.length === 0 ? (
                  <div className="procurement-review-empty">No active vendors matched the current scope selection.</div>
                ) : visibleVendors.map((vendor) => (
                  <label key={vendor.vendor_id} className="procurement-rfp-vendor-card">
                    <input
                      type="checkbox"
                      checked={selectedVendorSet.has(vendor.vendor_id)}
                      onChange={() => toggleSelectedValue('selected_vendor_ids', vendor.vendor_id)}
                    />
                    <div>
                      <div className="procurement-review-cell-title">{vendor.company_name}</div>
                      <div className="procurement-review-cell-meta">
                        {vendor.vendor_id} | {vendor.trade || '--'} | {vendor.scope_ids.join(', ') || 'no scope ids'}
                      </div>
                      <div className="procurement-review-cell-meta">{vendor.contact_summary}</div>
                      <div className="procurement-review-cell-meta">
                        Resolved emails: {vendor.resolved_emails.join(', ') || '--'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : null}
          </FormSection>

          <FormSection title="Exclusions">
            <div className="procurement-review-note">
              Building and unit exclusions are stored in package context and applied before the
              SBF rows are generated. Excluded buildings remove all units inside those buildings.
            </div>

            <div className="procurement-rfp-exclusion-grid">
              <div className="procurement-rfp-checklist">
                <div className="procurement-review-cell-title">Buildings</div>
                {selectedProject?.buildings?.length ? selectedProject.buildings.map((buildingId) => (
                  <label key={buildingId} className="procurement-rfp-checklist-row">
                    <input
                      type="checkbox"
                      checked={form.building_exclusions.includes(buildingId)}
                      onChange={() => toggleSelectedValue('building_exclusions', buildingId)}
                    />
                    <span>{buildingId}</span>
                  </label>
                )) : <div className="procurement-review-empty">Select a project to load buildings.</div>}
              </div>

              <div className="procurement-rfp-checklist">
                <div className="procurement-review-cell-title">Units</div>
                {selectedProject?.unit_rows?.length ? selectedProject.unit_rows.map((unit) => {
                  const unitNumber = String(unit.unit_number || '').trim()
                  const buildingId = String(unit.building_id || '').trim()
                  const disabled = form.building_exclusions.includes(buildingId)
                  return (
                    <label key={`${buildingId}-${unitNumber}`} className={`procurement-rfp-checklist-row${disabled ? ' procurement-rfp-checklist-row--disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={form.unit_exclusions.includes(unitNumber) || disabled}
                        disabled={disabled}
                        onChange={() => toggleSelectedValue('unit_exclusions', unitNumber)}
                      />
                      <span>{unitNumber} | {buildingId || '--'} | {unit.plan_type || '--'}</span>
                    </label>
                  )
                }) : <div className="procurement-review-empty">Select a project to load units.</div>}
              </div>
            </div>
          </FormSection>

          <FormSection title="RFP Output Preview">
            <div className="procurement-review-summary-grid">
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Template Family</span>
                <span className="procurement-review-stat-value">{rfpModel.template_family}</span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Included Units</span>
                <span className="procurement-review-stat-value">{rfpModel.project?.unit_count || 0}</span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Included Buildings</span>
                <span className="procurement-review-stat-value">{rfpModel.project?.building_count || 0}</span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Plan Rows</span>
                <span className="procurement-review-stat-value">{rfpModel.plan_rows.length}</span>
              </div>
            </div>

            <div className="procurement-review-note">
              Project: {rfpModel.project?.project_name || '--'} | Address: {rfpModel.project?.project_address || '--'} | Location: {[rfpModel.project?.project_city, rfpModel.project?.project_state].filter(Boolean).join(', ') || '--'} | Included buildings: {Array.from(includedBuildingSet).join(', ') || '--'}
            </div>

            <div className="procurement-review-note">
              Story mix: {rfpModel.project?.story_mix || '--'} | Home type: {rfpModel.package_context?.home_type_label || '--'} | Expected deliveries: {rfpModel.package_context?.delivery_summary_override || [rfpModel.package_context?.delivery_start_quarter, rfpModel.package_context?.delivery_start_year].filter(Boolean).join(' ') || '--'}
            </div>

            {rfpModel.plan_rows.length ? (
              <div className="procurement-rfp-table-wrap">
                <table className="table procurement-review-table procurement-rfp-plan-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Units</th>
                      <th>Buildings</th>
                      <th>Plan SF</th>
                      <th>Included Buildings</th>
                      <th>Included Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfpModel.plan_rows.map((row) => (
                      <tr key={row.line_key}>
                        <td>{row.plan_type}</td>
                        <td>{row.included_unit_count}</td>
                        <td>{row.included_building_count}</td>
                        <td>{row.plan_sf ?? '--'}</td>
                        <td>{row.included_buildings.join(', ') || '--'}</td>
                        <td>{row.included_unit_numbers.join(', ') || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="procurement-review-empty">
                No plan rows are available yet. Select a project and remove enough exclusions to keep at least one included unit.
              </div>
            )}
          </FormSection>

          <FormSection title="Package Issuance">
            <div className="procurement-review-note">
              Package issuance is explicit in this slice. Staff should intentionally create and
              issue the package row here before any later slice tries to bind real vendor-portal
              prep to `Send`. The current `Send` action below stays on the draft-email baseline
              and still carries the placeholder upload language.
            </div>

            <div className="procurement-review-summary-grid">
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Package ID</span>
                <span className="procurement-review-stat-value">{rfpModel.package_draft?.package_id || '--'}</span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Status</span>
                <span className="procurement-review-stat-value">
                  {packageRecord?.status || (packageReviewLoading ? 'loading' : 'not issued')}
                </span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Bid Due</span>
                <span className="procurement-review-stat-value">{rfpModel.package_context?.due_at || '--'}</span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Requested Vendors</span>
                <span className="procurement-review-stat-value">{rfpModel.send_groups.length}</span>
              </div>
            </div>

            <div className="procurement-review-note">
              Existing package row: {packageRecord?.package_id || 'none yet'} | Issued at: {packageRecord?.issued_at || '--'} | Created by: {packageRecord?.created_by || user?.email || '--'}
            </div>

            {!rfpModel.package_context?.due_at ? (
              <div className="banner warn">
                <p>Set the bid due date before creating and issuing the package.</p>
              </div>
            ) : null}

            {!rfpModel.send_groups.length ? (
              <div className="banner warn">
                <p>Select at least one vendor before creating and issuing the package.</p>
              </div>
            ) : null}

            {packageReviewError ? (
              <div className="banner warn">
                <p>{packageReviewError}</p>
              </div>
            ) : null}

            {packageIssueError ? (
              <div className="banner warn">
                <p>{packageIssueError}</p>
              </div>
            ) : null}

            {packageIssueMessage ? (
              <div className="banner success">
                <p>{packageIssueMessage}</p>
              </div>
            ) : null}

            {packageIsIssued ? (
              <div className="procurement-review-note">
                This package is now explicitly issued. Later portal-link wiring can bind to this
                package row without hiding persistence inside `Send`.
              </div>
            ) : null}

            <div className="actions procurement-review-actions">
              <button
                type="button"
                className="btn primary"
                onClick={handleIssuePackage}
                disabled={packageIssueDisabled}
              >
                {packageIssueLoading
                  ? 'Issuing Package...'
                  : packageIsIssued
                    ? 'Package Issued'
                    : packageRecord?.package_id
                    ? 'Issue Package'
                    : 'Create And Issue Package'}
              </button>
            </div>
          </FormSection>

          <FormSection title="Send Groups And Workbook Generation">
            <div className="procurement-review-note">
              This slice is <code>{PROCUREMENT_SEND_OUTPUT_CLASSIFICATION}</code>. Each vendor row
              keeps the existing browser-generated `.xlsx` workbook path and the controlled
              Outlook / Microsoft 365 draft action that uses the current user mailbox context.
              Attachments remain manual by default. Once the package is explicitly issued, workbook
              generation and `Send` now both prepare the real vendor-specific portal link so the
              email and the SBF cover sheet carry the same controlled return URL.
            </div>

            {rfpModel.send_groups.length === 0 ? (
              <div className="procurement-review-empty">Select one or more vendors to generate vendor-specific SBF workbooks.</div>
            ) : (
              <div className="procurement-review-response-list">
                {rfpModel.send_groups.map((sendGroup) => (
                  <div key={sendGroup.vendor_id} className="procurement-review-response-card">
                    <div className="procurement-review-response-grid">
                      <div>
                        <div className="procurement-review-cell-title">{sendGroup.company_name}</div>
                        <div className="procurement-review-cell-meta">{sendGroup.vendor_id}</div>
                      </div>
                      <div>
                        <div className="procurement-review-cell-title">Resolved Emails</div>
                        <div className="procurement-review-cell-meta">{sendGroup.resolved_emails.join(', ') || '--'}</div>
                      </div>
                      <div>
                        <div className="procurement-review-cell-title">Contact Summary</div>
                        <div className="procurement-review-cell-meta">{sendGroup.contact_summary}</div>
                      </div>
                      <div>
                        <div className="procurement-review-cell-title">Draft Subject</div>
                        <div className="procurement-review-cell-meta">{sendGroup.draft_email.subject}</div>
                      </div>
                    </div>
                    <div className="procurement-review-note">
                      Outlook draft classification: {sendGroup.draft_email.classification} | manual workbook attachment | package {sendGroup.portal_preparation.package_id}
                    </div>
                    {preparedPortalLinks[sendGroup.vendor_id] ? (
                      <div className="procurement-review-note">
                        Prepared vendor portal link: {preparedPortalLinks[sendGroup.vendor_id]}
                      </div>
                    ) : null}
                    <div className="actions procurement-review-actions">
                      <button
                        type="button"
                        className="btn primary"
                        onClick={() => handleDownloadVendorWorkbook(sendGroup)}
                        disabled={!rfpModel.plan_rows.length}
                      >
                        Generate SBF .xlsx
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => handleOpenVendorDraft(sendGroup)}
                        disabled={
                          sendingVendorId === sendGroup.vendor_id
                          || !sendGroup.resolved_emails.length
                          || !rfpModel.project?.project_id
                          || !rfpModel.scope?.scope_id
                          || !packageIsIssued
                        }
                      >
                        {sendingVendorId === sendGroup.vendor_id ? 'Preparing...' : 'Send'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FormSection>
        </>
      ) : null}
    </div>
  )
}

export default ProcurementRfpDesigner


